import { visible } from "../event/visibility-observer";
import { html, nothing, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import { getPictureSourceSets } from "./picture-url";

const PADDING = 50;

class Picture extends HTMLElement {
	constructor() {
		super();
		this.loaded = false;
		this.registered = false;
	}

	static get observedAttributes() {
		return ["onhold"];
	}

	connectedCallback() {
		if (this.getAttribute("onhold") === null) {
			this.register();
		}
	}

	attributeChangedCallback(name, oldValue, newValue) {
		if (name === "onhold" && newValue === null) {
			this.register();
		}
	}

	// Register this widget by the visibility observer for lazy loading.
	async register() {
		if (this.registered) {
			return;
		}

		this.registered = true;
		if (!this.hasAttribute("force-load")) {
			await visible(this, PADDING, 10);
		}

		// Remove initial content. It'll be included in the template until the image
		// is loaded. This feature is intended for content placeholders, where an
		// empty image can be added to make room and prevent layout shifts.
		this.initialNodes = Array.from(this.childNodes);
		this.initialNodes.forEach(child => {
			this.removeChild(child);
		});

		this.render();
	}

	render() {
		const [filename, alt, stageCropping] = ["filename", "alt", "crop"].map(
			attr => this.getAttribute(attr),
		);
		const showLoader = this.hasAttribute("show-loader");
		const filenamePerBreakpoint =
			this.hasAttribute("filename-per-breakpoint") &&
			JSON.parse(this.getAttribute("filename-per-breakpoint"));

		const { fallback, sources } = getPictureSourceSets(
			filename,
			this.getAttribute("sizes-key"),
			filenamePerBreakpoint,
			stageCropping,
		);

		// Finally wrap the sources around a picture element and add the
		// fallback.
		const template = html`${showLoader
				? html`<div
						class="content-placeholder ${classMap({ hidden: this.loaded })}"
					></div>`
				: nothing}${!this.loaded ? this.initialNodes : nothing}<picture>
				${sources.map(
					({ mediaQuery, srcset }) =>
						html`<source media="${mediaQuery}" srcset="${srcset}" />`,
				)}
				<img
					src="${fallback}"
					alt="${alt}"
					class="${classMap({ loaded: this.loaded })}"
					@load=${this.onLoad.bind(this)}
				/>
			</picture>`;

		render(template, this, { host: this });
	}

	onLoad() {
		this.loaded = true;
		this.render();
	}
}

customElements.define("esc-picture", Picture);
